import { Validator } from "vee-validate";
import ImageFileCompression from "@/commons/utils/ImageFileCompression";
import FileReaderWrapper from "@/commons/utils/FileReaderWrapper";
import PdfUtil, { PdfUtilResult, PdfUtilResultStatus } from "@/commons/utils/PdfUtil";
import { getType } from "mime";

/**
 * ### 介護記録共通ビジネスロジック
 *
 * - 介護記録登録画面、介護記録一括登録画面の共通処理を切り出したクラス
 */
export default class CareimageCommonService {
  /** バリデーター */
  private validator: Validator;
  /** バリデーターで使用するフィールド名 */
  private fieldName: string;

  /**
   * ### コンストラクター
   *
   * @param validator バリデーション用クラス
   * @param fieldName バリデーションで使用するフィールド名
   */
  constructor(validator: Validator, fieldName: string) {
    this.validator = validator;
    this.fieldName = fieldName;
  }

  /**
   * ### 登録対象の介護記録ファイルを作成する
   *
   * - PDFを優先ソートしてからファイルを読み込む
   * - エラーがあった場合、無効な操作として空の配列を返す
   *
   * @param files 登録対象候補のファイル
   */
  public async createAddFile(files: Array<File>, name: string, limit: number, currentFileCount: number): Promise<Array<CareimageFileModel>> {
    // 現在のバリデーションエラーをリセットしてから、バリデーションを実施する
    this.validator.errors.remove(this.fieldName);

    // 追加可能な残ファイル数を計算し、追加不可ならエラーメッセージを登録して即終了
    // 追加可能な残ファイル数は後続処理でも「あと何ファイル追加できるか」の計算で使用する
    let canAddFileCount: number = limit - currentFileCount;
    if (canAddFileCount < 1) {
      this.validator.errors.add({ field: this.fieldName, msg: `${name}様の最大登録ファイル数以上は登録できません` });
      return [];
    }

    // PDFを優先ソートしてからファイルを読み込む
    const sortedFiles: Array<File> = this.sortFiles(files.slice());

    // 親コンポーネントに渡すファイルを作成する
    // PDFの場合は1ページを1枚のjpegに変換して、介護記録1枚分として扱う
    const addFiles: Array<CareimageFileModel> = [];

    for (const file of sortedFiles) {
      // ファイルのメディアタイプチェック
      // APIと同じライブラリでチェックすることで整合性を担保している
      const mime: string | null = this.getType(file.name);
      if (mime == null || mime != file.type) {
        const errorMessage: string = `${name}様の${file.name}はjpgまたはpdf以外の拡張子のため登録できません`;
        this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
        return [];
      }

      // 追加可能かチェック
      if (canAddFileCount < 1) {
        this.validator.errors.add({ field: this.fieldName, msg: `${name}様の最大登録ファイル数以上は登録できません` });
        return [];
      }

      // pdfの場合は全ページをjpeg化し、Fileに変換してkeyを作って配列に追加する
      if (file.type == "application/pdf") {
        const pdf: PdfUtilResult = await PdfUtil.getPdfDocument(file);

        // 読込んだPDFをバリデーションしてNGなら空配列を返す
        const pdfValid: boolean = this.pdfValidation(pdf, name);
        if (!pdfValid || pdf.data == undefined) {
          return [];
        }

        for (let i = 1; i <= pdf.data.numPages; i++) {
          // 追加可能かチェック
          if (canAddFileCount < 1) {
            this.validator.errors.add({ field: this.fieldName, msg: `${name}様の最大登録ファイル数以上は登録できません` });
            return [];
          }

          const jpeg: PdfUtilResult = await PdfUtil.convertPdfDocumentToJpeg(pdf.data, pdf.name, i);
          if (jpeg.dataUrl) {
            const file: any = {
              dataUrl: jpeg.dataUrl,
              key: "",
              name: jpeg.name
            };
            addFiles.push(file);
            canAddFileCount--;
          }
        }

        // 使用したPDFJSドキュメントを削除する
        pdf.data.destroy();
      }
      // jpegの場合はkeyを作成して配列に追加する
      else if (file.type == "image/jpeg") {
        try {
          const compressedFile: File | undefined = await this.compressImage(file);
          if (!compressedFile) {
            continue;
          }

          const dataUrl: string = await this.encode(compressedFile);
          const addFile: any = {
            dataUrl: dataUrl,
            key: "",
            name: file.name
          };
          addFiles.push(addFile);
          canAddFileCount--;
        } catch (error) {
          const _error: Error = error as Error;
          this.validator.errors.add({ field: this.fieldName, msg: `${name}様の${file.name}は${_error.message}` });
          return [];
        }
      }
      // PDFまたはjpeg以外のファイルの場合はエラーとして扱う
      else {
        const errorMessage: string = `${name}様の${file.name}はjpgまたはpdf以外の拡張子のため登録できません`;
        this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
        return [];
      }
    }

    return addFiles;
  }

  /**
   * ## ファイルのパスまたは拡張子からContent-Typeを取得する
   */
  public getType(fileString: string): string | null {
    /**
     * getTypeの引数にはファイルパスが必要
     * 先頭文字列が「.」の場合、拡張子でありファイルパスではないため、先頭にファイル名を付けてファイルパスにする
     * 先頭文字列が「.」ではない場合、ファイルパスとする
     */
    let contentType: string | null = "";
    if (fileString[0] == ".") {
      contentType = getType(`file${fileString}`);
    } else {
      contentType = getType(`${fileString}`);
    }

    return contentType;
  }

  /**
   * ### ファイルのソート
   *
   * - アプリ仕様により、pdfは優先的に前にする
   * - その他はファイル名でソートする
   */
  public sortFiles(files: Array<File>): Array<File> {
    const sortedFiles: Array<File> = files.sort((a: File, b: File) => {
      const nameA: string = a.name;
      const typeA: string = a.type;

      const nameB: string = b.name;
      const typeB: string = b.type;

      // aがpdfの場合、aが先
      if (typeA == "application/pdf" && typeB != "application/pdf") {
        return -1;
      }
      // bがpdfの場合、bが先
      if (typeA != "application/pdf" && typeB == "application/pdf") {
        return 1;
      }
      // a、bが両方pdfまたは両方pdfでない場合、名前順でソート
      if (nameA > nameB) {
        return 1;
      } else {
        return -1;
      }
    });

    return sortedFiles;
  }

  /**
   * ### 画像ファイルのサイズを圧縮する
   */
  public async compressImage(file: File): Promise<File | undefined> {
    const compressedFile: File | undefined = await ImageFileCompression.imageCompression(file, "image/jpeg", 1 * 1024 * 1024, 4096, 4096);

    return compressedFile ? compressedFile : undefined;
  }

  /**
   * ### PDFのバリデーション
   */
  public pdfValidation(pdf: PdfUtilResult, name: string): boolean {
    if (pdf.status == PdfUtilResultStatus.NO_ERROR) {
      // 変換したPDFのデータがない場合は非対応PDFとして扱う
      if (!pdf.data) {
        this.validator.errors.add({
          field: this.fieldName,
          msg: `${name}様の${name}は非対応PDFのため登録できません`
        });

        return false;
      }

      return true;
    }
    // パスワード付きPDFの場合はエラー
    else if (pdf.status == PdfUtilResultStatus.LOCKED) {
      this.validator.errors.add({
        field: this.fieldName,
        msg: `${name}様の${pdf.name}はパスワード付きPDFのため登録できません`
      });

      return false;
    }
    // 非対応PDFの場合はエラー
    else {
      this.validator.errors.add({
        field: this.fieldName,
        msg: `${name}様の${pdf.name}は非対応PDFのため登録できません`
      });

      return false;
    }
  }

  /**
   * ファイルをBase64エンコードする
   *
   * @param file ファイル
   */
  public async encode(file: File): Promise<string> {
    let fileUrl: string = "";
    fileUrl = await FileReaderWrapper.readAsDataURL(file);

    return fileUrl;
  }
}

/**
 * ### 介護記録画像Model
 *
 * - v-for等で使用するため、URLだけでなくファイル名やkey名を持たせたobjectにする
 */
export interface CareimageFileModel extends File {
  /** URL */
  dataUrl: string;
  /** vuedraggableで並び替える際に必須となる一意キー */
  key: string;
  /** ファイル名 */
  name: string;
}
